import React, { useContext, useState } from 'react';
import i18next from 'core/i18n';
import { AgConversation, AgConversationConnection, AGFNotificationCustomFilters } from '@indigo-ag/schema';
import { SelectChangeEvent, TableActions } from '@indigo-ag/component-module';
import styled from 'styled-components';

import { NewPage } from 'core/components';
import { calculateAfterPagination, LoadingWrapper, TABLE_ACTIONS, utils, CreditAnalystSelector } from 'shared';
import { getConversationsForNotifications } from '../../queries';
import { NotificationsList } from './NotificationsList';
import {
  AGFCreditAnalystAssignedFilterKeys,
  CREDIT_ANALYST_DEFAULT_VALUES
} from 'features/brazil-grower/shared-br-grower';
import { ColumnId } from '../../components/AgFinancingQueue';
import { UserContext } from 'core/components/GateWay/GateWay';

const CreditAnalystSelectorWrapper = styled.div`
  width: 10rem;
`;

export const NotificationPage: React.FunctionComponent = () => {
  const rowsPerPage = 10;

  /**
   * User Context
   */
  const { isCreditAnalyst, userId } = useContext(UserContext);

  /**
   * State
   */
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedCreditAnalyst, setSelectedCreditAnalyst] = useState<string | null>(isCreditAnalyst ? userId : null);
  const [analystSearchValue, setAnalystSearchValue] = useState<AGFNotificationCustomFilters['creditAnalystAssigned']>(
    selectedCreditAnalyst ? { creditAnalystAssignedId: selectedCreditAnalyst } : undefined
  );

  const updateCreditAnalyst = (event: SelectChangeEvent) => {
    const selection = event.target.value;
    setSelectedCreditAnalyst(selection);
    setCurrentPage(0);

    setAnalystSearchValue(
      selection
        ? AGFCreditAnalystAssignedFilterKeys.has(selection as CREDIT_ANALYST_DEFAULT_VALUES)
          ? { [selection]: true }
          : { creditAnalystAssignedId: selection }
        : undefined
    );
  };

  /**
   * Callbacks
   */
  const renderAgFinanceQueue = ({
    data
  }: {
    data: { financingAgFinancingConversationNotifications: AgConversationConnection };
  }) => {
    const allNodes: AgConversation[] = data?.financingAgFinancingConversationNotifications?.edges.map(e => e.node);
    const pagination = {
      limit: rowsPerPage,
      offset: currentPage * rowsPerPage,
      total: data?.financingAgFinancingConversationNotifications?.totalCount || 0
    };

    const handleTableAction = (action: TableActions<ColumnId>) => {
      if (action.type === TABLE_ACTIONS.NEXT_PAGE) {
        return setCurrentPage(currentPage + 1);
      }

      if (action.type === TABLE_ACTIONS.PREVIOUS_PAGE) {
        return setCurrentPage(currentPage - 1);
      }
    };

    return (
      <NotificationsList
        onTableAction={handleTableAction}
        pagination={pagination}
        notifications={allNodes.map(
          ({
            id,
            previewMessage,
            agFinancing: { id: financingId, phase2, grower, orderInfo, collateralType, creditAnalystAssigned }
          }) => {
            const opportunityInfo = orderInfo?.opportunities ? orderInfo?.opportunities[0] : undefined;
            return {
              id,
              phase2,
              financingId,
              previewMessage,
              name: grower?.name,
              creditAnalystAssigned: creditAnalystAssigned,
              state: grower?.state?.name,
              guarantyType: collateralType,
              contractValue: opportunityInfo?.contractValue
            };
          }
        )}
      />
    );
  };

  return (
    <NewPage
      title={i18next.t('allNotifications')}
      contentRight={
        <CreditAnalystSelectorWrapper>
          <CreditAnalystSelector
            initialOptions={[
              {
                name: utils.text.firstLetterUppercase(i18next.t('unassigned')),
                value: CREDIT_ANALYST_DEFAULT_VALUES.UNASSIGNED
              },
              {
                name: utils.text.firstLetterUppercase(i18next.t('assigned')),
                value: CREDIT_ANALYST_DEFAULT_VALUES.ASSIGNED
              }
            ]}
            label={utils.text.toTitleCase(i18next.t('creditAnalyst'))}
            onChange={updateCreditAnalyst}
            preSelectedAnalystId={selectedCreditAnalyst || undefined}
          />
        </CreditAnalystSelectorWrapper>
      }
    >
      <LoadingWrapper
        query={getConversationsForNotifications}
        queryOptions={{
          variables: {
            pagination: { first: rowsPerPage, after: calculateAfterPagination(currentPage, rowsPerPage) },
            filters: {
              creditAnalystAssigned: analystSearchValue
            }
          },
          fetchPolicy: 'cache-and-network'
        }}
      >
        {renderAgFinanceQueue}
      </LoadingWrapper>
    </NewPage>
  );
};
